import type { AxiosResponse } from 'axios';
import type { User, AuthResponse } from '../types/api';
import { config, getApiUrl } from '../config';
import { apiClient } from '../lib/apiClient'; // Use centralized apiClient
import { normalizeUserRole } from '../utils/userUtils';

// Auth interceptors are now handled in lib/apiClient.ts

// Token storage utilities
const TOKEN_STORAGE_KEY = config.auth.tokenStorageKey;
const USER_STORAGE_KEY = config.auth.userStorageKey;

export const storeTokens = (tokens: AuthResponse): void => {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  } catch (error) {
    // Silent fail
  }
};

export const getStoredTokens = (): AuthResponse | null => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    
    const tokens = JSON.parse(stored) as AuthResponse;
    
    // Check if tokens are expired
    if (new Date(tokens.expires_at) <= new Date()) {
      clearStoredTokens();
      return null;
    }
    
    return tokens;
  } catch (error) {
    clearStoredTokens();
    return null;
  }
};

export const clearStoredTokens = (): void => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  } catch (error) {
  }
};

export const storeUser = (user: User): void => {
  try {
    // Normalize user to ensure role_info is always present
    const normalizedUser = normalizeUserRole(user);
    
    if (normalizedUser) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(normalizedUser));
      
      // Verify storage
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      if (stored) {
        JSON.parse(stored);
      }
    }
  } catch (error) {
    // Error storing user
  }
};

export const getStoredUser = (): User | null => {
  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    
    const user = JSON.parse(stored);
    // Normalize to ensure role_info is present
    const normalizedUser = normalizeUserRole(user);
    return normalizedUser;
  } catch (error) {
    return null;
  }
};

// Authentication API methods
export interface LoginCredentials {
  email: string;
  password: string;
}

export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  try {
    
    // Actual Rails API response format (not OpenAPI format)
    interface RailsAuthResponse {
      success: boolean;
      status: string;
      data: {
        user: User;
        tokens: {
          access_token: string;
          refresh_token: string;
          expires_at: string;
        };
      };
      message: string;
      timestamp: string;
    }
    
    const response: AxiosResponse<RailsAuthResponse> = await apiClient.post(
      getApiUrl('/auth/login'),
      credentials
    );

    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Login failed');
    }
    
    // Convert Rails format to expected format
    const authData: AuthResponse = {
      token: response.data.data.tokens.access_token,
      refresh_token: response.data.data.tokens.refresh_token,
      expires_at: response.data.data.tokens.expires_at,
      user: normalizeUserRole(response.data.data.user) || response.data.data.user
    };
    
    // Store tokens and user data
    storeTokens(authData);
    storeUser(authData.user);

    return authData;
  } catch (error: any) {
    
    if (error.response && error.response.status === 401) {
      const errorMsg = (error.response.data && 
                       error.response.data.error && 
                       error.response.data.error.message) || 'Invalid email or password';
      throw new Error(errorMsg);
    } else if (error.response && 
               error.response.data && 
               error.response.data.error && 
               error.response.data.error.message) {
      throw new Error(error.response.data.error.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Login failed. Please try again.');
    }
  }
};

export const logout = async (): Promise<void> => {
  try {
    const tokens = getStoredTokens();
    if (tokens && tokens.token) {
      // Call logout endpoint to invalidate token on server
      await apiClient.delete(getApiUrl('/auth/logout')); // OpenAPI uses DELETE
    }
  } catch (error) {
    // Continue with local logout even if API call fails
  } finally {
    // Always clear local storage
    clearStoredTokens();
  }
};

export const refreshAuthToken = async (_refreshToken: string): Promise<AuthResponse> => {
  try {
    // OpenAPI spec shows direct AuthResponse, no request body needed
    const response: AxiosResponse<AuthResponse> = await apiClient.post(
      getApiUrl('/auth/refresh')
    );

    return response.data;
  } catch (error: any) {
    throw new Error('Session expired. Please login again.');
  }
};

export const getCurrentUser = async (): Promise<User> => {
  try {
    // OpenAPI spec shows direct User response, not wrapped
    const response: AxiosResponse<User> = await apiClient.get(getApiUrl('/users/profile'));

    const user = normalizeUserRole(response.data) || response.data;
    
    storeUser(user);

    return user;
  } catch (error: any) {
    throw new Error('Failed to get user profile');
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    await apiClient.post(getApiUrl('/auth/reset_password'), { // OpenAPI uses reset_password
      email,
    });
  } catch (error: any) {
    
    if (error.response && error.response.status === 404) {
      throw new Error('Email not found');
    } else if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    } else {
      throw new Error('Password reset failed. Please try again.');
    }
  }
};

export const updatePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  try {
    await apiClient.put(getApiUrl('/auth/update_password'), { // OpenAPI uses update_password
      current_password: currentPassword,
      new_password: newPassword,
    });
  } catch (error: any) {
    
    if (error.response && error.response.status === 401) {
      throw new Error('Current password is incorrect');
    } else if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    } else {
      throw new Error('Password update failed. Please try again.');
    }
  }
};

// Utility functions
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const hasValidToken = (): boolean => {
  const tokens = getStoredTokens();
  return tokens !== null && !isTokenExpired(tokens.token);
};

// Export getApiUrl for convenience - apiClient should be imported from lib/apiClient
export { getApiUrl };